import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function TeamPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: '顧問團隊 | 平衡計分卡專業顧問 | BSC四大構面專家',
    description: '平衡計分卡顧問團隊，匯集財務、顧客、內部流程、學習成長四大構面專家。柯昆宏會計師、賴銘堃行銷專家、洪玉萍顧問、王可帆教練，為您提供全方位策略諮詢服務。',
    keywords: '平衡計分卡顧問,BSC專家,策略顧問,財務顧問,行銷專家,流程顧問,企業教練',
    canonical: `${siteUrl}/team`,
    ogTitle: '顧問團隊 | 平衡計分卡專業顧問',
    ogDescription: '四大構面專業顧問，為您提供全方位策略諮詢服務',
    ogType: 'website',
    schemaId: 'team',
    schemaData: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${siteUrl}/team#webpage`,
        url: `${siteUrl}/team`,
        name: '顧問團隊 | 平衡計分卡專業顧問 | BSC四大構面專家',
        description: '平衡計分卡顧問團隊，匯集財務、顧客、內部流程、學習成長四大構面專家。',
        inLanguage: 'zh-TW',
        isPartOf: {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: '平衡計分卡顧問公司',
          inLanguage: 'zh-TW'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '平衡計分卡顧問團隊',
        description: '服務業與製造業BSC顧問專家列表',
        numberOfItems: 6,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@type': 'Person',
              name: '柯昆宏',
              jobTitle: '會計師',
              description: '擁有超過20年財務管理與會計實務經驗，專精企業財務規劃、成本控制策略與財務績效指標設計。',
              knowsAbout: ['財務績效指標設計', '成本效益分析', '預算管理系統', '財務風險控管']
            }
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@type': 'Person',
              name: '賴銘堃',
              jobTitle: '行銷專家',
              description: '數位行銷與品牌策略領域專家，擅長顧客關係管理、市場定位與品牌價值提升。',
              knowsAbout: ['顧客滿意度指標', '市場定位策略', '品牌價值管理', '顧客關係經營']
            }
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@type': 'Person',
              name: '洪玉萍',
              jobTitle: '永續顧問',
              description: '專注於製造業永續發展與ESG策略規劃，協助企業建立完整的碳排放管理機制與永續供應鏈體系。',
              knowsAbout: ['ESG策略規劃', '碳排放管理', '永續供應鏈', '綠色製造導入']
            }
          },
          {
            '@type': 'ListItem',
            position: 4,
            item: {
              '@type': 'Person',
              name: '王可帆',
              jobTitle: '教練',
              description: '組織發展與人才培育教練，專注於企業文化轉型與領導力發展。',
              knowsAbout: ['人才發展策略', '組織文化塑造', '領導力培訓', '知識管理系統']
            }
          },
          {
            '@type': 'ListItem',
            position: 5,
            item: {
              '@type': 'Person',
              name: '林宏舜',
              jobTitle: '製造業內部流程顧問',
              description: '曾任鴻海集團內部經理，擁有豐富的大型製造業跨廠區管理與流程標準化實戰經驗。',
              knowsAbout: ['製程標準化', '跨廠區管理', '供應鏈整合', '生產效率優化']
            }
          },
          {
            '@type': 'ListItem',
            position: 6,
            item: {
              '@type': 'Person',
              name: '王可帆',
              jobTitle: '教練（製造業學習成長）',
              description: '組織發展與人才培育教練，專注於製造業人才發展與組織轉型。',
              knowsAbout: ['技術人才培育', '工廠文化轉型', '技能矩陣建立', '創新改善文化']
            }
          }
        ]
      }
    ]
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const serviceConsultants = [
    {
      name: '柯昆宏',
      title: '會計師',
      perspective: '財務構面',
      color: '#1A3A52',
      icon: 'ri-money-dollar-circle-line',
      avatar: 'https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/43793d55b273ac413da77df4b40303d1.jpeg',
      description: '擁有超過20年財務管理與會計實務經驗，專精企業財務規劃、成本控制策略與財務績效指標設計。協助眾多企業建立完善的財務管理體系，實現財務目標與股東價值最大化。',
      expertise: ['財務績效指標設計', '成本效益分析', '預算管理系統', '財務風險控管']
    },
    {
      name: '賴銘堃',
      title: '行銷專家',
      perspective: '顧客構面',
      color: '#D4AF37',
      icon: 'ri-user-heart-line',
      avatar: 'https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/f4c4d0c10d32d94a7661ac2afaba840f.jpeg',
      description: '數位行銷與品牌策略領域專家，擅長顧客關係管理、市場定位與品牌價值提升。曾協助多家企業成功轉型，建立以顧客為中心的經營模式，大幅提升顧客滿意度與忠誠度。',
      expertise: ['顧客滿意度指標', '市場定位策略', '品牌價值管理', '顧客關係經營']
    },
    {
      name: '洪玉萍',
      title: '顧問',
      perspective: '內部流程',
      color: '#50C878',
      icon: 'ri-settings-3-line',
      avatar: 'https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/89948aa086849afb4e10fbe94eeb8e7a.jpeg',
      description: '流程優化與營運管理專家，專注於協助企業建立高效營運系統與品質管理機制。擁有豐富的跨產業輔導經驗，善於發掘流程瓶頸並提出創新解決方案，提升整體營運效率。',
      expertise: ['流程優化設計', '品質管理系統', '營運效率提升', '供應鏈管理']
    },
    {
      name: '王可帆',
      title: '教練',
      perspective: '學習成長',
      color: '#9B59B6',
      icon: 'ri-lightbulb-line',
      avatar: 'https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/7ecb265607d2f47ecf30a6b0ffcea7ca.jpeg',
      description: '組織發展與人才培育教練，專注於企業文化轉型與領導力發展。透過系統化的培訓方法，協助企業建立學習型組織，激發員工潛能，打造持續創新與成長的組織能力。',
      expertise: ['人才發展策略', '組織文化塑造', '領導力培訓', '知識管理系統']
    }
  ];

  const manufacturingConsultants = [
    {
      name: '柯昆宏',
      title: '會計師',
      perspective: '財務構面',
      color: '#1A3A52',
      icon: 'ri-money-dollar-circle-line',
      avatar: 'https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/43793d55b273ac413da77df4b40303d1.jpeg',
      description: '擁有超過20年財務管理與會計實務經驗，深耕製造業財務管理，熟悉製造業成本結構與資本密集特性。專精產線投資評估、製造成本控制與財務績效優化，協助多家傳統製造業成功轉型升級，提升整體財務競爭力。',
      expertise: ['製造成本控制', '資本支出規劃', '產線投資報酬', '財務績效優化']
    },
    {
      name: '洪玉萍',
      title: '永續顧問',
      perspective: '永續構面',
      color: '#2E8B57',
      icon: 'ri-leaf-line',
      avatar: 'https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/89948aa086849afb4e10fbe94eeb8e7a.jpeg',
      description: '專注於製造業永續發展與ESG策略規劃，協助企業建立完整的碳排放管理機制與永續供應鏈體系。擁有豐富的綠色製造導入經驗，協助多家製造業企業達成減碳目標，並將永續指標納入平衡計分卡框架，實現永續競爭力。',
      expertise: ['ESG策略規劃', '碳排放管理', '永續供應鏈', '綠色製造導入']
    },
    {
      name: '林宏舜',
      title: '製造業內部流程顧問',
      perspective: '內部流程',
      color: '#50C878',
      icon: 'ri-settings-3-line',
      avatar: 'https://public.readdy.ai/ai/img_res/073e9c29ba29d8f9eea9212214281c92.jpg',
      description: '曾任鴻海集團內部經理，擁有豐富的大型製造業跨廠區管理與流程標準化實戰經驗。深諳複雜製造環境下的流程優化與供應鏈整合，擅長將大型企業的管理精髓轉化為中小製造業可落地執行的內部流程改善方案。',
      expertise: ['製程標準化', '跨廠區管理', '供應鏈整合', '生產效率優化']
    },
    {
      name: '王可帆',
      title: '教練',
      perspective: '學習成長',
      color: '#9B59B6',
      icon: 'ri-lightbulb-line',
      avatar: 'https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/7ecb265607d2f47ecf30a6b0ffcea7ca.jpeg',
      description: '組織發展與人才培育教練，專注於製造業人才發展與組織轉型。透過技能矩陣、多能工培訓與改善提案制度，協助製造業企業打造持續學習與創新改善的組織文化，激發員工潛能，建立永續成長的學習型組織。',
      expertise: ['技術人才培育', '工廠文化轉型', '技能矩陣建立', '創新改善文化']
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/">
                <img src="https://public.readdy.ai/ai/img_res/9fa41e12-cf6f-466e-a521-df80e2d2b6a9.png" alt="平衡計分卡顧問" className="h-12 w-auto cursor-pointer" />
              </Link>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>首頁</Link>
              <Link to="/#about" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>關於平衡計分卡</Link>
              <Link to="/#services" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>服務內容</Link>
              <Link to="/team" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#D4AF37] border-b-2 border-[#D4AF37]'}`}>顧問團隊</Link>
              <Link to="/#cases" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>成功案例</Link>
              <Link to="/#contact" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}>聯絡我們</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/#contact" className="hidden md:block bg-[#D4AF37] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#C19B2B] transition-colors whitespace-nowrap cursor-pointer">
                免費諮詢
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-6 py-4 space-y-4">
              <Link 
                to="/" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                首頁
              </Link>
              <Link 
                to="/#about" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                關於平衡計分卡
              </Link>
              <Link 
                to="/#services" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                服務內容
              </Link>
              <Link 
                to="/team" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-[#D4AF37] font-medium cursor-pointer"
              >
                顧問團隊
              </Link>
              <Link 
                to="/#cases" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                成功案例
              </Link>
              <Link 
                to="/#contact" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-[#D4AF37] font-medium cursor-pointer"
              >
                聯絡我們
              </Link>
              <Link 
                to="/#contact" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block bg-[#D4AF37] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#C19B2B] transition-colors text-center cursor-pointer"
              >
                免費諮詢
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=professional%20business%20team%20collaboration%20in%20modern%20office%20environment%2C%20strategic%20planning%20meeting%20with%20diverse%20consultants%2C%20elegant%20corporate%20setting%20with%20warm%20lighting%20and%20sophisticated%20atmosphere%2C%20emphasizing%20teamwork%20and%20expertise&width=1920&height=800&seq=teamhero1&orientation=landscape" 
            alt="Team Background" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A3A52]/85 via-[#2C5F7F]/75 to-[#1A3A52]/85"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          <div className="inline-block border border-white/30 text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wider">
            EXPERT TEAM
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            專業顧問團隊
          </h1>
          <p className="text-xl text-gray-200 mb-4">
            四大構面 · 全方位策略支援
          </p>
          <div className="w-20 h-1 bg-[#D4AF37] mx-auto"></div>
        </div>
      </section>

      {/* Team Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A3A52] mb-6">顧問團隊</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto mt-4">
              結合財務、顧客、流程、成長四大專業領域，為您提供全方位策略諮詢
            </p>
          </div>

          {/* 服務業BSC顧問 */}
          <div className="mb-20">
            <div className="flex items-center gap-4 mb-12">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#1A3A52] rounded-xl flex items-center justify-center">
                  <i className="ri-store-2-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1A3A52]">服務業 BSC 顧問</h3>
                  <p className="text-gray-500 text-sm mt-0.5">專注服務業策略管理與績效提升</p>
                </div>
              </div>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {serviceConsultants.map((consultant, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border border-gray-100"
                >
                  <div className="p-8">
                    <div
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold mb-6"
                      style={{ backgroundColor: consultant.color }}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${consultant.icon} text-lg`}></i>
                      </div>
                      {consultant.perspective}
                    </div>

                    <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                      <div className="flex-shrink-0">
                        <div
                          className="w-36 h-36 rounded-full overflow-hidden border-4"
                          style={{ borderColor: consultant.color }}
                        >
                          <img
                            src={consultant.avatar}
                            alt={consultant.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      </div>

                      <div className="flex-1 text-center md:text-left">
                        <h3 className="text-2xl font-bold text-[#1A3A52] mb-1">{consultant.name}</h3>
                        <p className="text-gray-500 text-base mb-4">{consultant.title}</p>
                        <p className="text-gray-600 text-sm leading-relaxed mb-5">
                          {consultant.description}
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                          {consultant.expertise.map((skill, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 製造業BSC顧問 */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-12">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-xl flex items-center justify-center">
                  <i className="ri-building-2-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1A3A52]">製造業 BSC 顧問</h3>
                  <p className="text-gray-500 text-sm mt-0.5">深耕製造業精實管理與生產效能提升</p>
                </div>
              </div>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {manufacturingConsultants.map((consultant, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border border-gray-100"
                >
                  <div className="p-8">
                    <div
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold mb-6"
                      style={{ backgroundColor: consultant.color }}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${consultant.icon} text-lg`}></i>
                      </div>
                      {consultant.perspective}
                    </div>

                    <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                      <div className="flex-shrink-0">
                        <div
                          className="w-36 h-36 rounded-full overflow-hidden border-4"
                          style={{ borderColor: consultant.color }}
                        >
                          <img
                            src={consultant.avatar}
                            alt={consultant.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      </div>

                      <div className="flex-1 text-center md:text-left">
                        <h3 className="text-2xl font-bold text-[#1A3A52] mb-1">{consultant.name}</h3>
                        <p className="text-gray-500 text-base mb-4">{consultant.title}</p>
                        <p className="text-gray-600 text-sm leading-relaxed mb-5">
                          {consultant.description}
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                          {consultant.expertise.map((skill, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-r from-[#1A3A52] to-[#2C5F7F] rounded-3xl p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              準備好開始您的策略轉型了嗎？
            </h2>
            <p className="text-gray-200 text-lg mb-8">
              立即預約免費諮詢，讓專業顧問為您量身打造解決方案
            </p>
            <Link 
              to="/#contact" 
              className="inline-block bg-[#D4AF37] text-white px-10 py-4 rounded-full text-lg font-bold hover:bg-[#C19B2B] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              預約諮詢
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A3A52] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <img src="https://public.readdy.ai/ai/img_res/9fa41e12-cf6f-466e-a521-df80e2d2b6a9.png" alt="Logo" className="h-12 mb-4 brightness-0 invert" />
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                專業的平衡計分卡顧問團隊，協助企業將策略轉化為可衡量的行動方案，實現永續成長與卓越績效。
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer">
                  <i className="ri-youtube-fill text-lg"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4 text-base">快速連結</h3>
              <ul className="space-y-3">
                <li><Link to="/#about" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">關於我們</Link></li>
                <li><Link to="/#services" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">服務項目</Link></li>
                <li><Link to="/team" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">顧問團隊</Link></li>
                <li><Link to="/#cases" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">成功案例</Link></li>
                <li><Link to="/#contact" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer">聯絡我們</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4 text-base">我們的服務</h3>
              <ul className="space-y-3">
                <li><span className="text-gray-400 text-sm">平衡計分卡導入</span></li>
                <li><span className="text-gray-400 text-sm">策略規劃諮詢</span></li>
                <li><span className="text-gray-400 text-sm">KPI指標設計</span></li>
                <li><span className="text-gray-400 text-sm">教育訓練課程</span></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4 text-base">聯絡資訊</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-400 text-sm">
                  <i className="ri-mail-line"></i>
                  contact@bsc-consulting.com
                </li>
                <li className="flex items-center gap-3 text-gray-400 text-sm">
                  <i className="ri-phone-line"></i>
                  02-1234-5678
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-xs">© 2025 平衡計分卡顧問公司. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#D4AF37] text-xs transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
